/**
 * Group Action - WHAT Section
 *
 * Edit mode UI for adding/removing from a group.
 * Used inline within AutomationCard when editing the "What" section.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-add-group.hbs
 *
 * Fields:
 * - reminderOnId: Selected group ID
 * - removeFromGroup: Boolean - whether to remove instead of add
 * - price: Price ID for charging (when not removing)
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Checkbox } from 'src/components/Checkbox';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { fetchAllGroups } from 'src/models/group/group';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';

import { ActionWhatSectionProps } from '../automationActions';

/**
 * Group WHAT Section - Edit mode
 */
export const GroupWhatSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  const addRemoveOptions = [
    { value: 'false', label: t('automations.group.addToGroup') },
    { value: 'true', label: t('automations.group.removeFromGroup') },
  ];

  // Fetch available groups
  const { data: groups = [], isLoading } = useQuery({
    queryKey: ['groups'],
    queryFn: () => fetchAllGroups(),
    staleTime: 60000,
  });

  const groupOptions = groups.map((g) => ({
    value: g.id.toString(),
    label: g.name,
  }));

  const selectedGroup = groups.find(
    (g) => g.id.toString() === automation.reminderOnId?.toString()
  );

  // Build price options including lifetime price (id: -1) if available
  // V2 reference: group.js priceIds computed property
  const priceOptions = (() => {
    const options: { value: string; label: string }[] = [];

    // Add lifetime price option if group has oneTimePayment
    if (selectedGroup?.oneTimePayment) {
      options.push({
        value: '-1',
        label: `${t('automations.group.lifetime')}: ${selectedGroup.oneTimePayment}`,
      });
    }

    // Add stripe plan options from paymentOptions
    if (selectedGroup?.paymentOptions) {
      selectedGroup.paymentOptions
        .filter((p) => p.id != null)
        .forEach((p) => {
          options.push({
            value: String(p.id),
            label: p.name || String(p.id),
          });
        });
    }

    return options;
  })();

  const isRemove = automation.removeFromGroup === true;

  // V2 stores charge and addManager flags in subject as "charge_addManager" (e.g., "true_false")
  const subjectParts = String(automation.subject || '').split('_');
  const chargeForGroup = subjectParts[0] === 'true';
  const addManager = subjectParts[1] === 'true';

  return (
    <Stack spacing={2}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        {/* Add/Remove selector */}
        <Box sx={{ minWidth: 150 }}>
          <TextField
            size="medium"
            value={isRemove ? 'true' : 'false'}
            options={addRemoveOptions}
            onChange={(e) => {
              const shouldRemove = e.target.value === 'true';
              setFieldValue(`automations.${index}.removeFromGroup`, shouldRemove);
              // Clear price if removing
              if (shouldRemove) {
                setFieldValue(`automations.${index}.price`, null);
              }
            }}
          />
        </Box>

        {/* Group selector */}
        <Box sx={{ minWidth: 200 }}>
          <FormSelect
            name={`automations.${index}.reminderOnId`}
            options={groupOptions}
            prompt={isLoading ? t('automations.common.loading') : t('automations.group.selectGroup')}
            onChange={(e) => {
              setFieldValue(`automations.${index}.reminderOnId`, e.target.value);
              // V2 sets reminderOnType to 'Group'
              setFieldValue(`automations.${index}.reminderOnType`, 'Group');
            }}
          />
        </Box>
      </Box>

      {/* Charge option - only when adding */}
      {!isRemove && selectedGroup && (
        <Stack spacing={1}>
          <Box>
            <Checkbox
              label={t('automations.group.chargeForGroup')}
              checked={chargeForGroup}
              onChange={(e) => {
                const newCharge = e.target.checked;
                // Store as "charge_addManager" format in subject field
                setFieldValue(
                  `automations.${index}.subject`,
                  `${newCharge}_${addManager}`
                );
                // Clear price if unchecking
                if (!newCharge) {
                  setFieldValue(`automations.${index}.price`, null);
                }
              }}
            />
          </Box>

          {chargeForGroup && priceOptions.length > 0 && (
            <Box display="flex" alignItems="center" gap={1}>
              <Box>{t('automations.group.price')}</Box>
              <Box sx={{ minWidth: 150 }}>
                <FormSelect
                  name={`automations.${index}.price`}
                  options={priceOptions}
                  prompt={t('automations.group.selectPrice')}
                />
              </Box>
            </Box>
          )}

          {/* Add manager option */}
          <Box>
            <Checkbox
              label={t('automations.group.addManagerIfDependent')}
              checked={addManager}
              onChange={(e) => {
                const newAddManager = e.target.checked;
                // Store as "charge_addManager" format in subject field
                setFieldValue(
                  `automations.${index}.subject`,
                  `${chargeForGroup}_${newAddManager}`
                );
              }}
            />
          </Box>
        </Stack>
      )}
    </Stack>
  );
};

/**
 * Group What Summary Component
 */
export const GroupWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const { data: groups = [] } = useQuery({
    queryKey: ['groups'],
    queryFn: () => fetchAllGroups(),
    staleTime: 60000,
  });

  if (!automation.reminderOnId) {
    return <i>{t('automations.group.noGroupSelected')}</i>;
  }

  const group = groups.find(
    (g) => g.id.toString() === automation.reminderOnId?.toString()
  );
  const groupName = group?.name || `Group #${automation.reminderOnId}`;
  const isRemove = Boolean(automation.removeFromGroup);

  // V2 stores charge flag in subject as "charge_addManager"
  const subjectParts = String(automation.subject || '').split('_');
  const chargeForGroup = subjectParts[0] === 'true';

  // Get price display - V2 shows "Lifetime Price" for price -1
  const getPriceDisplay = () => {
    if (!chargeForGroup) return null;
    if (automation.price === -1 || String(automation.price) === '-1') {
      return t('automations.group.lifetimePrice');
    }
    return automation.price || t('automations.group.selectPrice');
  };

  return (
    <>
      {isRemove ? t('automations.group.removeFrom') : t('automations.group.assign')} <b>{groupName}</b>
      {chargeForGroup && (
        <>
          {' '}
          <span style={{ fontWeight: 'bold' }}>
            {t('automations.group.chargingForGroup')}: {getPriceDisplay()}
          </span>
        </>
      )}
    </>
  );
};
